// Unified locale contract — the single source of truth every renderer obeys.
//
// Two rendering worlds share this file:
//   1. The React tree (login / app / settings …) via `I18nProvider` + `useI18n`.
//   2. The SSR marketing landing, enhanced by the vanilla `landing.js` script
//      that runs *before* React hydrates.
//
// They diverge in how they render, but must agree on the language set, the
// `localStorage` key + values, detection order, and the `<html lang>` mapping —
// otherwise switching language on one surface corrupts the other. Keep this
// module free of React (and any DOM-at-import-time) so both can import it.

export const SUPPORTED_LOCALES = ["en", "zh-Hant", "zh-Hans", "ja"] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

// Shared across React and landing.js. Do not rename without migrating both.
export const LOCALE_STORAGE_KEY = "salesko_lang";

// `<html lang>` attribute value (BCP 47) per locale.
export const LOCALE_HTML_LANG: Record<Locale, string> = {
  en: "en",
  "zh-Hant": "zh-Hant",
  "zh-Hans": "zh-Hans",
  ja: "ja"
};

// Short label rendered inside the language switcher chips.
export const LOCALE_LABEL: Record<Locale, string> = {
  en: "EN",
  "zh-Hant": "繁",
  "zh-Hans": "简",
  ja: "日"
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

// Infer a locale from a `navigator.language`-style tag. Traditional Chinese
// regions (TW/HK/MO) and explicit `Hant` map to zh-Hant; any other `zh` is
// zh-Hans; `ja` is Japanese; everything else falls back to English.
export function detectLocaleFromLanguageTag(languageTag: string | undefined): Locale {
  const tag = (languageTag || "").toLowerCase();
  if (tag.startsWith("zh")) {
    if (tag.includes("hant") || tag.includes("tw") || tag.includes("hk") || tag.includes("mo")) {
      return "zh-Hant";
    }
    return "zh-Hans";
  }
  if (tag.startsWith("ja")) {
    return "ja";
  }
  return "en";
}

// Read the persisted locale. `"zh"` is the legacy landing.js value for
// Simplified Chinese; map it forward once so pre-migration visitors keep their
// choice. This is a bounded read-time migration, not an open compatibility
// path — remove it once no `"zh"` values remain in the wild.
export function readStoredLocale(): Locale | null {
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (raw === "zh") {
      return "zh-Hans";
    }
    return isLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeStoredLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* localStorage unavailable (private mode / SSR) — non-fatal. */
  }
}

// Resolve the initial locale: persisted choice first, then the browser's
// preferred language, then the default. Safe to call on the client only;
// during SSR callers must render `DEFAULT_LOCALE` and resolve after mount to
// avoid hydration mismatch (see `I18nProvider`).
export function resolveInitialLocale(): Locale {
  const stored = readStoredLocale();
  if (stored) {
    return stored;
  }
  if (typeof navigator !== "undefined") {
    return detectLocaleFromLanguageTag(navigator.language);
  }
  return DEFAULT_LOCALE;
}
